import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'

const AGORA_ACCENT = '#856FC8'

const QUEST_TYPES: Record<string, { label: string; color: string }> = {
  research: { label: 'Research', color: '#6FB8D6' },
  build: { label: 'Build', color: '#5AC884' },
  analyze: { label: 'Analyze', color: '#B265D6' },
  review: { label: 'Review', color: '#E8935A' },
  creative: { label: 'Creative', color: '#D65A93' },
  social: { label: 'Social', color: '#6FB8D6' },
}

const STATUS_COLORS: Record<string, string> = {
  proposed: '#6B7280', discussing: '#E6C85A', active: '#6FB8D6',
  completed: '#5AC884', evaluated: '#B265D6', awarded: '#E8935A', closed: '#4B5563',
}

const STATUS_STEPS = ['proposed', 'discussing', 'active', 'completed', 'evaluated', 'awarded']
const STATUS_LABELS: Record<string, string> = {
  proposed: 'Proposed', discussing: 'Discussing', active: 'Active',
  completed: 'Completed', evaluated: 'Evaluated', awarded: 'Awarded',
}

const DIM_LABELS: Record<string, string> = {
  com: 'Competence', soc: 'Social', cre: 'Creative',
  wis: 'Wisdom', srv: 'Service', idn: 'Identity',
}

const DIM_COLORS: Record<string, string> = {
  com: '#E8935A', soc: '#6FB8D6', cre: '#B265D6',
  wis: '#E6C85A', srv: '#5AC884', idn: '#D65A93',
}

interface Phase {
  id: string
  title: string
  description: string
  status: 'pending' | 'active' | 'completed'
  tasks: { id: string; title: string; status: 'pending' | 'active' | 'completed'; completedAt?: string }[]
}

interface TimelineEvent {
  id: string
  timestamp: string
  type: string
  content: string
  actor: string
}

interface Deliverable {
  id: string
  title: string
  type: string
  path?: string
  url?: string
  description: string
  addedAt: string
  addedBy: string
}

interface Evaluation {
  summary: string
  score: number
  xpTokens: Record<string, number>
  evaluatedBy: string
  evaluatedAt: string
}

interface Quest {
  id: string
  title: string
  type: string
  description: string
  deliverable: string
  persona: string
  questGiver: string
  status: string
  difficulty: string
  createdAt: string
  completedAt?: string
  qualityScore?: number
  xpAwarded?: Record<string, number>
  phases?: Phase[]
  timeline?: TimelineEvent[]
  deliverables?: Deliverable[]
  evaluation?: Evaluation
  recommendation?: { score: number; summary: string; xpTokens: Record<string, number>; generatedAt: string }
  chatConversationId?: string
}

type TabKey = 'overview' | 'timeline' | 'chat' | 'deliverables' | 'evaluation'

export default function QuestDetailPage() {
  const { questId } = useParams<{ questId: string }>()
  const [quest, setQuest] = useState<Quest | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabKey>('overview')
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleText, setTitleText] = useState('')

  const loadQuest = useCallback(async () => {
    try {
      const res = await fetch(`/api/quests/${questId}`)
      if (!res.ok) throw new Error(`${res.status}`)
      setQuest(await res.json())
      setError(null)
    } catch (e: any) {
      setError(e.message || 'Failed to load quest')
    } finally {
      setLoading(false)
    }
  }, [questId])

  useEffect(() => { loadQuest() }, [loadQuest])

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading quest...</div>
  if (error || !quest) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Quest not found ({error})</div>

  const typeKeys = quest.type.includes('+') ? quest.type.split('+') : [quest.type]

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 24px' }}>
        {/* Back nav */}
        <Link to="/evolve" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none', display: 'block', marginBottom: 12 }}>
          ← Back to Evolve
        </Link>

        {/* Header */}
        <div style={{
          marginBottom: 24, padding: '20px 24px',
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 14, borderTop: `3px solid ${STATUS_COLORS[quest.status] || '#6B7280'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            <PersonaAvatar name={quest.persona} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingTitle ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <input
                    value={titleText}
                    onChange={e => setTitleText(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && titleText.trim()) {
                        await fetch(`/api/quests/${quest.id}`, {
                          method: 'PATCH', credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ title: titleText.trim() }),
                        })
                        setEditingTitle(false)
                        loadQuest()
                      }
                      if (e.key === 'Escape') setEditingTitle(false)
                    }}
                    style={{
                      fontFamily: 'Playfair Display, Georgia, serif', fontSize: 24, fontWeight: 700,
                      background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                      border: '1px solid var(--border)', borderRadius: 8, padding: '4px 12px',
                      flex: 1, outline: 'none',
                    }}
                    autoFocus
                  />
                  <button
                    onClick={async () => {
                      if (titleText.trim()) {
                        await fetch(`/api/quests/${quest.id}`, {
                          method: 'PATCH', credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ title: titleText.trim() }),
                        })
                        setEditingTitle(false)
                        loadQuest()
                      }
                    }}
                    style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#856FC8', color: 'white', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                  >Save</button>
                  <button onClick={() => setEditingTitle(false)} style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                </div>
              ) : (
                <h1
                  onClick={() => { setTitleText(quest.title); setEditingTitle(true) }}
                  style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 24, fontWeight: 700, margin: '0 0 6px 0', cursor: 'pointer' }}
                  title="Click to edit title"
                >
                  {quest.title}
                </h1>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 500 }}>{capitalize(quest.persona)}</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>Given by {capitalize(quest.questGiver)}</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                  background: quest.difficulty === 'hard' || quest.difficulty === 'expert' ? 'rgba(248,81,73,0.12)' : 'var(--bg-elevated)',
                  color: quest.difficulty === 'hard' ? '#f85149' : quest.difficulty === 'expert' ? '#da3633' : 'var(--text-muted)',
                  textTransform: 'capitalize',
                }}>
                  {quest.difficulty}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {typeKeys.map(k => {
                    const t = QUEST_TYPES[k.trim()]
                    return <span key={k} style={{
                      fontSize: 9, padding: '2px 8px', borderRadius: 8,
                      background: `${t?.color || '#6B7280'}15`, color: t?.color || '#6B7280',
                      fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
                      border: `1px solid ${t?.color || '#6B7280'}25`,
                    }}>{t?.label || k}</span>
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Progress track */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {STATUS_STEPS.map((step, i) => {
              const stepIdx = STATUS_STEPS.indexOf(quest.status)
              const isActive = i === stepIdx
              const isPast = i < stepIdx
              const color = STATUS_COLORS[step] || '#6B7280'
              return (
                <div key={step} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1,
                  }}>
                    <div style={{
                      width: isActive ? 14 : 10, height: isActive ? 14 : 10, borderRadius: '50%',
                      background: isPast || isActive ? color : 'var(--bg-elevated)',
                      border: `2px solid ${isPast || isActive ? color : 'var(--border)'}`,
                      transition: 'all 0.3s',
                      boxShadow: isActive ? `0 0 8px ${color}40` : 'none',
                    }} />
                    <span style={{
                      fontSize: 8, fontWeight: isActive ? 700 : 500,
                      color: isActive ? color : isPast ? 'var(--text-secondary)' : 'var(--text-muted)',
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      opacity: isActive || isPast ? 1 : 0.5,
                    }}>
                      {STATUS_LABELS[step]}
                    </span>
                  </div>
                  {i < STATUS_STEPS.length - 1 && (
                    <div style={{
                      height: 2, flex: 1, minWidth: 12,
                      background: isPast ? color : 'var(--border)',
                      transition: 'background 0.3s',
                      marginTop: -16,
                    }} />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Accept/Decline bar for proposed quests */}
        {quest.status === 'proposed' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
            background: 'rgba(133,111,200,0.06)', border: `1px solid ${AGORA_ACCENT}25`,
            borderRadius: 10, marginBottom: 16,
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
              This quest is waiting to be accepted by {capitalize(quest.persona)}.
            </span>
            <button
              onClick={async () => {
                await fetch(`/api/quests/${quest.id}`, {
                  method: 'PATCH', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ status: 'active' }),
                })
                loadQuest()
              }}
              style={{
                padding: '8px 20px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: '#5AC884', color: 'white', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Accept Quest
            </button>
            <button
              onClick={async () => {
                prompt('Reason for declining (optional):')
                await fetch(`/api/quests/${quest.id}`, {
                  method: 'PATCH', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ status: 'closed' }),
                })
                loadQuest()
              }}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Decline
            </button>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {([
            { key: 'overview' as TabKey, label: 'Overview', count: quest.phases?.length || 0 },
            { key: 'timeline' as TabKey, label: 'Timeline', count: quest.timeline?.length || 0 },
            { key: 'chat' as TabKey, label: 'Chat', count: 0 },
            { key: 'deliverables' as TabKey, label: 'Deliverables', count: quest.deliverables?.length || 0 },
            { key: 'evaluation' as TabKey, label: 'Evaluation', count: quest.evaluation ? 1 : 0 },
          ]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 18px', fontSize: 12, fontWeight: tab === t.key ? 600 : 400,
                background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                color: tab === t.key ? AGORA_ACCENT : 'var(--text-muted)',
                borderBottom: `2px solid ${tab === t.key ? AGORA_ACCENT : 'transparent'}`,
                marginBottom: -1,
                transition: 'color 0.15s, border-color 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {t.label}
              {t.count > 0 && (
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                  background: tab === t.key ? `${AGORA_ACCENT}20` : 'var(--bg-elevated)',
                  color: tab === t.key ? AGORA_ACCENT : 'var(--text-muted)',
                }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'overview' && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 400px', minWidth: 0 }}>
              <OverviewTab quest={quest} onUpdate={loadQuest} />
            </div>
            <div style={{ flex: '0 0 320px', minWidth: 280 }}>
              <WizardBox questId={quest.id} />
            </div>
          </div>
        )}
        {tab === 'timeline' && <TimelineTab quest={quest} onUpdate={loadQuest} />}
        {tab === 'chat' && <ChatTab quest={quest} onUpdate={loadQuest} />}
        {tab === 'deliverables' && <DeliverablesTab quest={quest} onUpdate={loadQuest} />}
        {tab === 'evaluation' && <EvaluationTab quest={quest} onUpdate={loadQuest} />}
      </div>
    </div>
  )
}

// ═══ Overview Tab — Quest description + Phases/Tasks ═══

function OverviewTab({ quest, onUpdate }: { quest: Quest; onUpdate: () => void }) {
  const [addingPhase, setAddingPhase] = useState(false)
  const [phaseTitle, setPhaseTitle] = useState('')
  const [phaseDesc, setPhaseDesc] = useState('')
  const [taskInputs, setTaskInputs] = useState<Record<string, string>>({})
  const [quickTask, setQuickTask] = useState('')
  const [editingBrief, setEditingBrief] = useState(false)
  const [briefText, setBriefText] = useState(quest.description)
  const [briefDeliverable, setBriefDeliverable] = useState(quest.deliverable)

  const phases = quest.phases || []

  const addQuickTask = async () => {
    if (!quickTask.trim()) return
    let updated = [...phases]
    let standalone = updated.find(p => p.id === 'standalone')
    if (!standalone) {
      standalone = { id: 'standalone', title: 'Additional Tasks', description: '', status: 'active' as const, tasks: [] }
      updated.push(standalone)
    }
    standalone.tasks.push({ id: `tk-${Date.now()}`, title: quickTask.trim(), status: 'pending' as const })
    standalone.status = 'active'

    const body: any = { phases: updated }
    if (['completed', 'awarded', 'evaluated'].includes(quest.status)) {
      body.status = 'active'
    }
    await fetch(`/api/quests/${quest.id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    setQuickTask('')
    onUpdate()
  }

  const addPhase = async () => {
    if (!phaseTitle.trim()) return
    const newPhase: Phase = {
      id: `ph-${Date.now()}`,
      title: phaseTitle.trim(),
      description: phaseDesc.trim(),
      status: 'pending',
      tasks: [],
    }
    const updated = [...phases, newPhase]
    await fetch(`/api/quests/${quest.id}/phases`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phases: updated }),
    })
    setPhaseTitle('')
    setPhaseDesc('')
    setAddingPhase(false)
    onUpdate()
  }

  const addTask = async (phaseId: string) => {
    const title = taskInputs[phaseId]?.trim()
    if (!title) return
    const updated = phases.map(p => {
      if (p.id !== phaseId) return p
      return { ...p, tasks: [...p.tasks, { id: `tk-${Date.now()}`, title, status: 'pending' as const }] }
    })
    await fetch(`/api/quests/${quest.id}/phases`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phases: updated }),
    })
    setTaskInputs(prev => ({ ...prev, [phaseId]: '' }))
    onUpdate()
  }

  const toggleTask = async (phaseId: string, taskId: string) => {
    const updated = phases.map(p => {
      if (p.id !== phaseId) return p
      return {
        ...p,
        tasks: p.tasks.map(t => {
          if (t.id !== taskId) return t
          return { ...t, status: t.status === 'completed' ? 'pending' as const : 'completed' as const, completedAt: t.status === 'completed' ? undefined : new Date().toISOString() }
        }),
      }
    })
    await fetch(`/api/quests/${quest.id}/phases`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phases: updated }),
    })
    onUpdate()
  }

  const togglePhaseStatus = async (phaseId: string) => {
    const updated = phases.map(p => {
      if (p.id !== phaseId) return p
      const next = p.status === 'completed' ? 'pending' : p.status === 'active' ? 'completed' : 'active'
      return { ...p, status: next as Phase['status'] }
    })
    await fetch(`/api/quests/${quest.id}/phases`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phases: updated }),
    })
    onUpdate()
  }

  const deleteTask = async (phaseId: string, taskId: string) => {
    const updated = phases.map(p => {
      if (p.id !== phaseId) return p
      return { ...p, tasks: p.tasks.filter(t => t.id !== taskId) }
    })
    await fetch(`/api/quests/${quest.id}/phases`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phases: updated }),
    })
    onUpdate()
  }

  const moveTask = async (fromPhaseId: string, taskId: string, toPhaseId: string) => {
    if (fromPhaseId === toPhaseId) return
    let taskToMove: any = null
    const updated = phases.map(p => {
      if (p.id === fromPhaseId) {
        taskToMove = p.tasks.find(t => t.id === taskId)
        return { ...p, tasks: p.tasks.filter(t => t.id !== taskId) }
      }
      return p
    }).map(p => {
      if (p.id === toPhaseId && taskToMove) {
        return { ...p, tasks: [...p.tasks, taskToMove] }
      }
      return p
    })
    await fetch(`/api/quests/${quest.id}/phases`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phases: updated }),
    })
    onUpdate()
  }

  return (
    <div>
      {/* Quest description */}
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 20, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: 0 }}>
            Quest Brief
          </h3>
          {!editingBrief && (
            <button onClick={() => setEditingBrief(true)} style={{ ...smallBtnStyle, padding: '4px 10px', fontSize: 10 }}>
              Edit
            </button>
          )}
        </div>
        {editingBrief ? (
          <div>
            <textarea
              value={briefText}
              onChange={e => setBriefText(e.target.value)}
              style={{
                ...inputSmall, width: '100%', minHeight: 120, resize: 'vertical',
                padding: '10px 12px', fontSize: 13, lineHeight: 1.6, boxSizing: 'border-box',
              }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              <strong>Expected deliverable:</strong>
              <input
                value={briefDeliverable}
                onChange={e => setBriefDeliverable(e.target.value)}
                style={{ ...inputSmall, width: '100%', marginTop: 4, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button
                onClick={async () => {
                  await fetch(`/api/quests/${quest.id}`, {
                    method: 'PATCH', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ description: briefText, deliverable: briefDeliverable }),
                  })
                  setEditingBrief(false)
                  onUpdate()
                }}
                style={{ ...smallBtnStyle, background: AGORA_ACCENT, color: 'white', border: 'none' }}
              >
                Save
              </button>
              <button onClick={() => { setBriefText(quest.description); setBriefDeliverable(quest.deliverable); setEditingBrief(false) }} style={smallBtnStyle}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
              <MarkdownRenderer content={quest.description} />
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
              <strong>Expected deliverable:</strong> {quest.deliverable}
            </div>
          </>
        )}
      </div>

      {/* Phases */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Phases & Tasks
          </h3>
          {!addingPhase && (
            <button onClick={() => setAddingPhase(true)} style={smallBtnStyle}>
              + Add Phase
            </button>
          )}
        </div>

        {phases.length === 0 && !addingPhase && (
          <div style={{
            padding: '40px 20px', textAlign: 'center',
            background: 'var(--bg-surface)', border: '1px dashed var(--border)',
            borderRadius: 12, color: 'var(--text-muted)',
          }}>
            <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.15 }}>📋</div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>No phases defined yet</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>The quest assignee should break this quest into phases and tasks.</div>
          </div>
        )}

        {phases.map((phase, i) => {
          const completedCount = phase.tasks.filter(t => t.status === 'completed').length
          return (
            <div key={phase.id} style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderLeft: `3px solid ${phase.status === 'completed' ? '#5AC884' : phase.status === 'active' ? '#6FB8D6' : 'var(--border)'}`,
              borderRadius: 10, padding: 16, marginBottom: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: phase.tasks.length > 0 ? 10 : 0 }}>
                <button
                  onClick={() => togglePhaseStatus(phase.id)}
                  style={{
                    width: 20, height: 20, borderRadius: '50%', border: `2px solid ${phase.status === 'completed' ? '#5AC884' : 'var(--border)'}`,
                    background: phase.status === 'completed' ? '#5AC884' : 'transparent',
                    color: 'white', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {phase.status === 'completed' ? '✓' : ''}
                </button>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, textDecoration: phase.status === 'completed' ? 'line-through' : 'none', opacity: phase.status === 'completed' ? 0.6 : 1 }}>
                    Phase {i + 1}: {phase.title}
                  </span>
                  {phase.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{phase.description}</div>}
                </div>
                {phase.tasks.length > 0 && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    {completedCount}/{phase.tasks.length}
                  </span>
                )}
              </div>

              {/* Tasks */}
              {phase.tasks.map(task => (
                <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 30, marginBottom: 4, group: 'task' } as any}>
                  <button
                    onClick={() => toggleTask(phase.id, task.id)}
                    style={{
                      width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${task.status === 'completed' ? '#5AC884' : 'var(--border)'}`,
                      background: task.status === 'completed' ? '#5AC884' : 'transparent',
                      color: 'white', fontSize: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    {task.status === 'completed' ? '✓' : ''}
                  </button>
                  <span style={{ fontSize: 12, flex: 1, textDecoration: task.status === 'completed' ? 'line-through' : 'none', opacity: task.status === 'completed' ? 0.6 : 1 }}>
                    {task.title}
                  </span>
                  {phases.length > 1 && (
                    <select
                      value={phase.id}
                      onChange={e => moveTask(phase.id, task.id, e.target.value)}
                      title="Move to phase"
                      style={{
                        fontSize: 9, padding: '1px 4px', borderRadius: 4,
                        background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                        border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      {phases.map((p, pi) => (
                        <option key={p.id} value={p.id}>
                          {p.id === 'standalone' ? 'Tasks' : `Phase ${pi + 1}`}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => deleteTask(phase.id, task.id)}
                    title="Delete task"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', fontSize: 11, padding: '0 3px', lineHeight: 1,
                      opacity: 0.5, flexShrink: 0,
                    }}
                    onMouseOver={e => (e.currentTarget.style.opacity = '1', e.currentTarget.style.color = '#f85149')}
                    onMouseOut={e => (e.currentTarget.style.opacity = '0.5', e.currentTarget.style.color = 'var(--text-muted)')}
                  >
                    ✕
                  </button>
                </div>
              ))}

              {/* Add task input */}
              <div style={{ display: 'flex', gap: 6, paddingLeft: 30, marginTop: 6 }}>
                <input
                  value={taskInputs[phase.id] || ''}
                  onChange={e => setTaskInputs(prev => ({ ...prev, [phase.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') addTask(phase.id) }}
                  placeholder="Add a task..."
                  style={{ ...inputSmall, flex: 1 }}
                />
                <button onClick={() => addTask(phase.id)} style={smallBtnStyle}>Add</button>
              </div>
            </div>
          )
        })}

        {/* Quick add task */}
        <div style={{
          display: 'flex', gap: 8, marginBottom: 12, padding: '10px 14px',
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 10,
        }}>
          <input
            value={quickTask}
            onChange={e => setQuickTask(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addQuickTask() }}
            placeholder="Add a task..."
            style={{ ...inputSmall, flex: 1, padding: '8px 12px' }}
          />
          <button onClick={addQuickTask} disabled={!quickTask.trim()} style={{
            ...smallBtnStyle,
            background: quickTask.trim() ? AGORA_ACCENT : 'var(--bg-elevated)',
            color: quickTask.trim() ? 'white' : 'var(--text-muted)',
            border: 'none', padding: '8px 16px',
          }}>
            + Add Task
          </button>
        </div>

        {/* Add phase form */}
        {addingPhase && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: 16,
          }}>
            <input
              value={phaseTitle}
              onChange={e => setPhaseTitle(e.target.value)}
              placeholder="Phase title..."
              style={{ ...inputSmall, width: '100%', marginBottom: 8 }}
              autoFocus
            />
            <input
              value={phaseDesc}
              onChange={e => setPhaseDesc(e.target.value)}
              placeholder="Brief description (optional)..."
              style={{ ...inputSmall, width: '100%', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={addPhase} style={{ ...smallBtnStyle, background: AGORA_ACCENT, color: 'white', border: 'none' }}>Add Phase</button>
              <button onClick={() => setAddingPhase(false)} style={smallBtnStyle}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ═══ Timeline Tab ═══

function TimelineTab({ quest, onUpdate }: { quest: Quest; onUpdate: () => void }) {
  const [note, setNote] = useState('')
  const [adding, setAdding] = useState(false)

  const timeline = (quest.timeline || []).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

  const addEvent = async () => {
    if (!note.trim() || adding) return
    setAdding(true)
    await fetch(`/api/quests/${quest.id}/timeline`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'note', content: note.trim(), actor: 'joel' }),
    })
    setNote('')
    setAdding(false)
    onUpdate()
  }

  const typeIcons: Record<string, string> = {
    status_change: '🔄', note: '📝', phase_complete: '✅', deliverable: '📎', evaluation: '⭐',
  }

  return (
    <div>
      {/* Add event */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addEvent() }}
          placeholder="Add a timeline note..."
          style={{ ...inputSmall, flex: 1, padding: '10px 14px' }}
        />
        <button onClick={addEvent} disabled={!note.trim() || adding} style={{
          ...smallBtnStyle, padding: '10px 20px',
          background: note.trim() ? AGORA_ACCENT : 'var(--bg-elevated)',
          color: note.trim() ? 'white' : 'var(--text-muted)',
          border: 'none',
        }}>
          {adding ? '...' : 'Add'}
        </button>
      </div>

      {timeline.length === 0 ? (
        <div style={{
          padding: '50px 20px', textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12,
        }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.15 }}>📜</div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>No timeline events yet</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Events are recorded automatically as the quest progresses.</div>
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{
            position: 'absolute', left: 11, top: 0, bottom: 0, width: 2,
            background: 'linear-gradient(180deg, var(--border), transparent)',
          }} />
          {timeline.map(event => {
            const iconColor = event.type === 'evaluation' ? '#B265D6'
              : event.type === 'phase_complete' ? '#5AC884'
              : event.type === 'status_change' ? '#6FB8D6'
              : 'var(--border)'
            return (
              <div key={event.id} style={{ position: 'relative', marginBottom: 16, paddingLeft: 20 }}>
                <div style={{
                  position: 'absolute', left: -6, top: 4, width: 22, height: 22, borderRadius: '50%',
                  background: 'var(--bg-base)', border: `2px solid ${iconColor}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
                }}>
                  {typeIcons[event.type] || '•'}
                </div>
                <div style={{
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: 14,
                  transition: 'border-color 0.2s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <PersonaAvatar name={event.actor} size={18} />
                    <span style={{ fontSize: 11, fontWeight: 600 }}>{capitalize(event.actor)}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatTimestamp(event.timestamp)}</span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{event.content}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ═══ Chat Tab ═══

function ChatTab({ quest, onUpdate }: { quest: Quest; onUpdate: () => void }) {
  const [messages, setMessages] = useState<any[]>([])
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [participants, setParticipants] = useState<string[]>([])
  const [inviteName, setInviteName] = useState('')
  const [showInvite, setShowInvite] = useState(false)
  const convId = quest.chatConversationId

  const loadMessages = useCallback(async () => {
    if (!convId) return
    try {
      const res = await fetch(`/api/quests/${quest.id}/chat`)
      const data = await res.json()
      setMessages(data?.messages || [])
    } catch {}
  }, [convId, quest.id])

  const loadParticipants = useCallback(async () => {
    if (!convId) return
    const senders = new Set<string>()
    messages.forEach((m: any) => { if (m.sender) senders.add(m.sender) })
    senders.add(quest.persona)
    senders.add('joel')
    setParticipants([...senders].sort())
  }, [convId, messages, quest.persona])

  useEffect(() => { loadMessages(); loadParticipants() }, [loadMessages, loadParticipants])

  const sendMessage = async () => {
    if (!text.trim() || sending || !convId) return
    setSending(true)
    try {
      await fetch(`/api/quests/${quest.id}/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender: 'joel', content: text.trim() }),
      })
      setText('')
      await loadMessages()
    } catch {}
    setSending(false)
  }

  const invitePersona = async () => {
    const name = inviteName.trim().toLowerCase()
    if (!name || !convId) return
    try {
      await fetch(`/api/conversations/${convId}/participants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant: name }),
      })
      setInviteName('')
      setShowInvite(false)
      await loadParticipants()
    } catch {}
  }

  const dismissPersona = async (name: string) => {
    if (!convId || name === quest.persona || name === 'joel') return
    try {
      await fetch(`/api/conversations/${convId}/participants/${name}`, {
        method: 'DELETE',
      })
      await loadParticipants()
    } catch {}
  }

  if (!convId) {
    return (
      <div style={{
        padding: '50px 20px', textAlign: 'center',
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12,
      }}>
        <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.15 }}>💬</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
          Quest Chat Room
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', opacity: 0.7, marginBottom: 16 }}>
          Start a chat room for this quest. {capitalize(quest.persona)} can invite other personas here for discussions.
        </div>
        <button
          onClick={async () => {
            try {
              const res = await fetch(`/api/quests/${quest.id}/start-chat`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
              })
              if (res.ok) {
                onUpdate()
              } else {
                const err = await res.json().catch(() => ({}))
                alert(`Could not start chat: ${err.error || res.status}`)
              }
            } catch { alert('Could not start chat — network error') }
          }}
          style={{
            padding: '10px 24px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: AGORA_ACCENT, color: 'white', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Start Quest Chat
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Participants bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px',
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>In room:</span>
        {participants.map(p => (
          <div key={p} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
            background: 'var(--bg-elevated)', borderRadius: 12, fontSize: 11,
          }}>
            <PersonaAvatar name={p} size={14} />
            <span>{capitalize(p)}</span>
            {p !== quest.persona && p !== 'joel' && (
              <button
                onClick={() => dismissPersona(p)}
                title={`Dismiss ${capitalize(p)}`}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 10, padding: '0 2px', lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
        {showInvite ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              value={inviteName}
              onChange={e => setInviteName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') invitePersona() }}
              placeholder="persona name..."
              style={{ ...inputSmall, width: 120, padding: '3px 8px', fontSize: 10 }}
              autoFocus
            />
            <button onClick={invitePersona} style={{ ...smallBtnStyle, padding: '3px 8px', fontSize: 10 }}>Invite</button>
            <button onClick={() => setShowInvite(false)} style={{ ...smallBtnStyle, padding: '3px 8px', fontSize: 10 }}>✕</button>
          </div>
        ) : (
          <button onClick={() => setShowInvite(true)} style={{ ...smallBtnStyle, padding: '3px 10px', fontSize: 10 }}>
            + Invite
          </button>
        )}
      </div>

      {/* Messages */}
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 16, maxHeight: 500, overflowY: 'auto', marginBottom: 12,
      }}>
        {messages.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            No messages yet. Start discussing the quest.
          </div>
        ) : (
          messages.map((msg: any, i: number) => (
            <div key={msg.id || i} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <PersonaAvatar name={msg.sender} size={18} />
                <span style={{ fontSize: 11, fontWeight: 500 }}>{capitalize(msg.sender)}</span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{(msg.timestamp || msg.created_at) ? formatTimestamp(msg.timestamp || msg.created_at) : ''}</span>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5, paddingLeft: 24 }}>
                <MarkdownRenderer content={msg.content} />
              </div>
            </div>
          ))
        )}
      </div>
      {/* Input */}
      <div style={{ display: 'flex', gap: 8 }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
          placeholder="Message about this quest..."
          rows={3}
          style={{
            ...inputSmall, flex: 1, padding: '12px 16px', fontSize: 13,
            lineHeight: 1.5, resize: 'vertical', minHeight: 60,
          }}
        />
        <button onClick={sendMessage} disabled={!text.trim() || sending} style={{
          padding: '10px 20px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: text.trim() ? AGORA_ACCENT : 'var(--bg-elevated)',
          color: text.trim() ? 'white' : 'var(--text-muted)',
          border: 'none', cursor: text.trim() ? 'pointer' : 'default', fontFamily: 'inherit',
          alignSelf: 'flex-end',
        }}>
          {sending ? '...' : 'Send'}
        </button>
      </div>

      {/* Link to full conversation room */}
      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <a
          href={`/room/${convId}`}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 11, color: AGORA_ACCENT, textDecoration: 'none' }}
        >
          Open full chat with Magic Box →
        </a>
      </div>
    </div>
  )
}

// ═══ Deliverables Tab ═══

function DeliverablesTab({ quest, onUpdate }: { quest: Quest; onUpdate: () => void }) {
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [filePath, setFilePath] = useState('')
  const [adding, setAdding] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const deliverables = quest.deliverables || []

  const addDeliverable = async () => {
    if (!title.trim() || adding) return
    setAdding(true)
    await fetch(`/api/quests/${quest.id}/deliverables`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title.trim(), description: desc.trim(), path: filePath.trim() || null, type: 'document' }),
    })
    setTitle('')
    setDesc('')
    setFilePath('')
    setShowForm(false)
    setAdding(false)
    onUpdate()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        {!showForm && <button onClick={() => setShowForm(true)} style={smallBtnStyle}>+ Add Deliverable</button>}
      </div>

      {showForm && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Deliverable title..." style={{ ...inputSmall, width: '100%', marginBottom: 8 }} autoFocus />
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)..." style={{ ...inputSmall, width: '100%', marginBottom: 8 }} />
          <input value={filePath} onChange={e => setFilePath(e.target.value)} placeholder="File path in vault (optional)..." style={{ ...inputSmall, width: '100%', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={addDeliverable} disabled={!title.trim() || adding} style={{ ...smallBtnStyle, background: AGORA_ACCENT, color: 'white', border: 'none' }}>
              {adding ? '...' : 'Add'}
            </button>
            <button onClick={() => setShowForm(false)} style={smallBtnStyle}>Cancel</button>
          </div>
        </div>
      )}

      {deliverables.length === 0 && !showForm ? (
        <div style={{
          padding: '50px 20px', textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12,
        }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.15 }}>📎</div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>No deliverables yet</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Attach documents, files, or links as the quest progresses.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {deliverables.map(d => (
            <DeliverableCard key={d.id} deliverable={d} questId={quest.id} />
          ))}
        </div>
      )}
    </div>
  )
}

function DeliverableCard({ deliverable: d, questId }: { deliverable: Deliverable; questId: string }) {
  const [expanded, setExpanded] = useState(false)
  const [content, setContent] = useState<string | null>(null)
  const [loadingContent, setLoadingContent] = useState(false)
  const [contentError, setContentError] = useState<string | null>(null)

  const typeIcons: Record<string, string> = { document: '📄', code: '💻', design: '🎨', data: '📊', conversation: '💬', other: '📎' }

  const loadContent = async () => {
    if (content !== null || !d.path) return
    setLoadingContent(true)
    try {
      const res = await fetch(`/api/quests/${questId}/deliverables/${d.id}/content`)
      if (res.ok) {
        const data = await res.json()
        setContent(data.content)
      } else {
        const err = await res.json().catch(() => ({}))
        setContentError(err.error || 'Could not load content')
      }
    } catch {
      setContentError('Network error')
    }
    setLoadingContent(false)
  }

  const handleClick = () => {
    if (d.url) {
      window.open(d.url, '_blank')
      return
    }
    setExpanded(!expanded)
    if (!expanded && d.path) loadContent()
  }

  return (
    <div style={{
      background: 'var(--bg-surface)', border: `1px solid ${expanded ? AGORA_ACCENT + '44' : 'var(--border)'}`,
      borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.2s',
    }}>
      <div
        onClick={handleClick}
        style={{
          padding: 14, display: 'flex', alignItems: 'center', gap: 12,
          cursor: 'pointer', transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
      >
        <span style={{ fontSize: 20 }}>{typeIcons[d.type] || '📎'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: AGORA_ACCENT }}>{d.title}</div>
          {d.description && (
            d.description.length > 200 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.7 }}>
                <MarkdownRenderer content={d.description} />
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{d.description}</div>
            )
          )}
          {d.path && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, fontFamily: "'SF Mono', Menlo, monospace" }}>{d.path}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>
            <div>{capitalize(d.addedBy)}</div>
            <div>{formatTimestamp(d.addedAt)}</div>
          </div>
          {(d.path || d.url) && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s', display: 'inline-block' }}>
              {d.url ? '↗' : '▶'}
            </span>
          )}
        </div>
      </div>
      {expanded && d.path && (
        <div style={{
          borderTop: '1px solid var(--border)', padding: 20,
          maxHeight: 600, overflowY: 'auto',
          background: 'var(--bg-base)',
        }}>
          {loadingContent && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading content...</div>}
          {contentError && <div style={{ color: '#f85149', fontSize: 12 }}>{contentError}</div>}
          {content && (
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <MarkdownRenderer content={content} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ═══ Evaluation Tab ═══

function EvaluationTab({ quest, onUpdate }: { quest: Quest; onUpdate: () => void }) {
  const rec = quest.recommendation
  const [summary, setSummary] = useState(quest.evaluation?.summary || rec?.summary || '')
  const [score, setScore] = useState(quest.evaluation?.score || rec?.score || 0)
  const [xpTokens, setXpTokens] = useState<Record<string, number>>(quest.evaluation?.xpTokens || rec?.xpTokens || {})
  const [submitting, setSubmitting] = useState(false)
  const [generating, setGenerating] = useState(false)

  const evaluated = !!quest.evaluation

  const generateRecommendation = async () => {
    setGenerating(true)
    try {
      const res = await fetch(`/api/quests/${quest.id}/recommend`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        if (data.recommendation) {
          setScore(data.recommendation.score || 0)
          setSummary(data.recommendation.summary || '')
          setXpTokens(data.recommendation.xpTokens || {})
        }
        onUpdate()
      }
    } catch {}
    setGenerating(false)
  }

  const submitEvaluation = async () => {
    if (submitting) return
    setSubmitting(true)
    await fetch(`/api/quests/${quest.id}/evaluate`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ summary: summary.trim(), score, xpTokens }),
    })
    setSubmitting(false)
    onUpdate()
  }

  if (evaluated && quest.evaluation) {
    const ev = quest.evaluation
    return (
      <div>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            Evaluation Complete
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            {[1, 2, 3, 4, 5].map(s => (
              <span key={s} style={{ fontSize: 20, opacity: s <= ev.score ? 1 : 0.2 }}>⭐</span>
            ))}
            <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 8 }}>{ev.score}/5</span>
          </div>
          {ev.summary && (
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: 16 }}>
              <MarkdownRenderer content={ev.summary} />
            </div>
          )}
          {Object.keys(ev.xpTokens).length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>XP Tokens Awarded</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(ev.xpTokens).filter(([, v]) => v > 0).map(([dim, val]) => {
                  const color = DIM_COLORS[dim] || AGORA_ACCENT
                  return (
                    <div key={dim} style={{
                      padding: '8px 16px', borderRadius: 10,
                      background: `${color}12`, border: `1px solid ${color}28`,
                      fontSize: 14, fontWeight: 700, color,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{ fontSize: 16 }}>+{val}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, opacity: 0.85 }}>
                        {DIM_LABELS[dim] || dim.toUpperCase()}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              Evaluated by {capitalize(ev.evaluatedBy)} · {formatTimestamp(ev.evaluatedAt)}
            </span>
            <button
              onClick={async () => {
                setGenerating(true)
                try {
                  const res = await fetch(`/api/quests/${quest.id}/recommend`, { credentials: 'include' })
                  if (res.ok) {
                    const data = await res.json()
                    if (data.recommendation) {
                      setScore(data.recommendation.score || 0)
                      setSummary(data.recommendation.summary || '')
                      setXpTokens(data.recommendation.xpTokens || {})
                    }
                    onUpdate()
                  }
                } catch {}
                setGenerating(false)
              }}
              disabled={generating}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: 'var(--bg-elevated)', color: AGORA_ACCENT,
                border: `1px solid ${AGORA_ACCENT}44`, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {generating ? 'Analyzing...' : '🔄 Re-evaluate'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>
          Evaluate Quest
        </h3>

        {/* Generate / Re-evaluate recommendation button */}
        {!evaluated && (
          <button onClick={generateRecommendation} disabled={generating} style={{
            padding: '10px 20px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: 'var(--bg-elevated)', color: AGORA_ACCENT,
            border: `1px solid ${AGORA_ACCENT}44`, cursor: 'pointer', fontFamily: 'inherit',
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {generating ? 'Analyzing quest...' : rec ? '🔄 Re-evaluate (fresh analysis)' : '✨ Generate Evaluation Recommendation'}
          </button>
        )}

        {rec && !evaluated && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 11, color: AGORA_ACCENT,
            background: `${AGORA_ACCENT}08`, border: `1px solid ${AGORA_ACCENT}20`,
            marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            ✨ AI recommendation pre-filled below. Review and adjust, then submit.
          </div>
        )}

        {/* Score */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
            QUALITY SCORE
            {rec && !evaluated && (
              <span
                onClick={() => setScore(rec.score)}
                style={{
                  marginLeft: 10, fontSize: 10, padding: '2px 8px', borderRadius: 6,
                  background: `${AGORA_ACCENT}12`, color: AGORA_ACCENT, cursor: 'pointer',
                  border: `1px solid ${AGORA_ACCENT}25`, fontWeight: 500,
                }}
                title="Click to use recommended score"
              >
                Recommended: {rec.score}/5
              </span>
            )}
          </label>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => setScore(s)} style={{
                fontSize: 24, background: 'none', border: 'none', cursor: 'pointer',
                opacity: s <= score ? 1 : 0.2, transition: 'opacity 0.15s',
              }}>
                ⭐
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>EVALUATION SUMMARY</label>
          <textarea
            value={summary}
            onChange={e => setSummary(e.target.value)}
            placeholder="How did the persona do? What stood out?"
            rows={12}
            style={{ ...inputSmall, width: '100%', resize: 'vertical', padding: '14px 16px', lineHeight: 1.7, fontSize: 13, minHeight: 180 }}
          />
        </div>

        {/* XP Tokens */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>XP TOKENS TO AWARD</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {Object.entries(DIM_LABELS).map(([key, label]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 60 }}>{label}</span>
                <input
                  type="number"
                  min={0}
                  value={xpTokens[key] || 0}
                  onChange={e => setXpTokens(prev => ({ ...prev, [key]: Math.max(0, Number(e.target.value) || 0) }))}
                  style={{ ...inputSmall, width: 60, padding: '4px 8px', textAlign: 'center' }}
                />
              </div>
            ))}
          </div>
        </div>

        <button onClick={submitEvaluation} disabled={submitting || score === 0} style={{
          padding: '10px 24px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: score > 0 ? AGORA_ACCENT : 'var(--bg-elevated)',
          color: score > 0 ? 'white' : 'var(--text-muted)',
          border: 'none', cursor: score > 0 ? 'pointer' : 'default', fontFamily: 'inherit',
        }}>
          {submitting ? 'Submitting...' : 'Submit Evaluation'}
        </button>
      </div>
    </div>
  )
}

// ═══ Quest Wizard Box ═══

function WizardBox({ questId }: { questId: string }) {
  const [actions, setActions] = useState<any[]>([])
  const [alert, setAlert] = useState<{ level: string; message: string } | null>(null)
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'assistant'; content: string }[]>([])
  const [chatInput, setChatInput] = useState('')
  const [chatSending, setChatSending] = useState(false)
  const [showLog, setShowLog] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [actRes, chatRes] = await Promise.all([
          fetch(`/api/wizard/actions?quest=${questId}&limit=20`),
          fetch(`/api/wizard/quest-chat/${questId}`),
        ])
        if (actRes.ok) {
          const d = await actRes.json()
          setActions(d.actions || [])
          setAlert(d.alert || null)
        }
        if (chatRes.ok) {
          const d = await chatRes.json()
          setChatHistory(d.messages || [])
        }
      } catch {}
      setLoading(false)
    }
    loadAll()
    const interval = setInterval(loadAll, 30000)
    return () => clearInterval(interval)
  }, [questId])

  const sendChat = async (msg: string) => {
    if (!msg.trim() || chatSending) return
    const newHistory = [...chatHistory, { role: 'user' as const, content: msg }]
    setChatHistory(newHistory)
    setChatInput('')
    setChatSending(true)
    try {
      const res = await fetch('/api/wizard/quest-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history: chatHistory, questId }),
      })
      const data = await res.json()
      if (data.reply) {
        setChatHistory([...newHistory, { role: 'assistant', content: data.reply }])
      } else {
        setChatHistory([...newHistory, { role: 'assistant', content: `Error: ${data.error || 'No response'}` }])
      }
    } catch {
      setChatHistory([...newHistory, { role: 'assistant', content: 'Connection error — please try again' }])
    }
    setChatSending(false)
  }

  const functionIcons: Record<string, string> = {
    acceptance_enforcer: '📬', deliverable_checker: '📎', stall_detector: '⏸',
    curriculum_navigator: '🧭', evaluation_prompter: '⭐', progress_reporter: '📊',
    prerequisite_coach: '🎓', reporting_assistant: '📝', pattern_detector: '🔍',
    velocity_monitor: '⚡', sleep_aware: '💤',
  }

  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 12, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'linear-gradient(135deg, rgba(133,111,200,0.06), rgba(92,200,132,0.03))',
      }}>
        <span style={{ fontSize: 14 }}>🧙</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#856FC8', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Quest Wizard
        </span>
        {alert && (
          <span style={{
            fontSize: 9, marginLeft: 'auto', fontWeight: 600,
            color: alert.level === 'green' ? '#5AC884' : alert.level === 'red' ? '#f85149' : '#E6C85A',
          }}>
            {alert.level === 'green' ? '✓' : alert.level === 'red' ? '!' : '~'} {alert.message}
          </span>
        )}
      </div>

      {/* Chat Messages */}
      <div style={{ maxHeight: 280, overflowY: 'auto', padding: 12 }}>
        {chatHistory.length === 0 && !loading && (
          <div style={{ padding: '16px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
            Ask the Wizard anything about this quest
          </div>
        )}
        {chatHistory.map((msg, i) => (
          <div key={i} style={{ marginBottom: 10, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
            <div style={{
              display: 'inline-block', maxWidth: '90%', padding: '8px 12px',
              borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
              background: msg.role === 'user' ? '#856FC8' : 'var(--bg-elevated)',
              color: msg.role === 'user' ? 'white' : 'var(--text-primary)',
              fontSize: 12, lineHeight: 1.5, textAlign: 'left',
            }}>
              {msg.role === 'assistant' ? <MarkdownRenderer content={msg.content} /> : msg.content}
            </div>
          </div>
        ))}
        {chatSending && (
          <div style={{ fontSize: 11, color: '#856FC8', padding: '4px 0' }}>🧙 thinking...</div>
        )}
      </div>

      {/* Chat Input */}
      <div style={{ borderTop: '1px solid var(--border)', padding: 8, display: 'flex', gap: 6 }}>
        <input
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && chatInput.trim() && !chatSending) { e.preventDefault(); sendChat(chatInput.trim()) } }}
          placeholder="Ask the Wizard..."
          style={{
            flex: 1, padding: '8px 12px', fontSize: 12,
            background: 'var(--bg-elevated)', color: 'var(--text-primary)',
            border: '1px solid var(--border)', borderRadius: 8,
            fontFamily: 'inherit', outline: 'none',
          }}
        />
        <button
          onClick={() => sendChat(chatInput.trim())}
          disabled={!chatInput.trim() || chatSending}
          style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
            background: chatInput.trim() ? '#856FC8' : 'var(--bg-elevated)',
            color: chatInput.trim() ? 'white' : 'var(--text-muted)',
            border: 'none', cursor: chatInput.trim() ? 'pointer' : 'default',
            fontFamily: 'inherit',
          }}
        >
          {chatSending ? '...' : 'Send'}
        </button>
      </div>

      {/* Activity Log — collapsible */}
      {actions.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <button
            onClick={() => setShowLog(!showLog)}
            style={{
              width: '100%', padding: '8px 16px',
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            <span style={{
              transition: 'transform 0.2s',
              transform: showLog ? 'rotate(90deg)' : 'rotate(0deg)',
              display: 'inline-block', fontSize: 8, color: 'var(--text-muted)',
            }}>▶</span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>
              Activity Log ({actions.length})
            </span>
          </button>
          {showLog && (
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {actions.map((action: any, i: number) => (
                <div key={action.id || i} style={{
                  padding: '8px 16px',
                  borderBottom: i < actions.length - 1 ? '1px solid var(--border)' : 'none',
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }}>
                    {functionIcons[action.function_name] || '🧙'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                      {action.message || action.action}
                    </div>
                    <div style={{ fontSize: 8, color: 'var(--text-muted)', marginTop: 1 }}>
                      {action.created_at ? formatTimestamp(action.created_at) : ''}
                      {action.function_name && (
                        <span style={{ marginLeft: 4, opacity: 0.6 }}>
                          {action.function_name.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ═══ Helpers ═══

const smallBtnStyle: React.CSSProperties = {
  padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600,
  background: 'var(--bg-elevated)', color: 'var(--text-muted)',
  border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
}

const inputSmall: React.CSSProperties = {
  padding: '6px 10px', fontSize: 12, borderRadius: 6,
  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontFamily: 'inherit', outline: 'none',
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(diff / 3600000)
  if (hrs < 24) return `${hrs}h ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })
}
